import type { Item } from '../core/hp/types';
import type { CatalogItem } from './catalog';
import { t } from '../i18n/I18n';

export interface ItemProperties {
  size: Item['size'];
  position: Item['position'];
  elevation: number;
  rotation: number;
}

export type ItemDraft = Record<string, string>;
/** A number as the inspector's field shows it: to the thousandth. */
export const draftText = (value: number) => String(Math.round(value * 1000) / 1000);

/** The inspector's fields for a piece: its size, place, height and turn, as text. */
export function itemDraft(item: Item): ItemDraft {
  return { width: draftText(item.size.w), depth: draftText(item.size.d), height: draftText(item.size.h), x: draftText(item.position.x), z: draftText(item.position.z),
    elevation: draftText(item.elevation), rotation: draftText(-item.rotation * 180 / Math.PI) };
}

/** A field of the draft as a number from `min` to `max`, or the inspector's «enter a number» message. */
export function readDraft(draft: ItemDraft, name: string, label: string, min: number, max: number): number {
  const raw = draft[name]?.trim(), value = Number(raw?.replace(',', '.'));
  if (!raw || !Number.isFinite(value) || value < min || value > max) throw new Error(t('#PropsEnterNumber', label, String(min), String(max)));
  return value;
}

/**
 * The piece's values from the inspector's draft; a mansard is drafted by its own fields. `onlyTyped`: a field left as it
 * came keeps the piece's own value, where its text to the thousandth would read back as a change of size, height or turn.
 */
export function itemDraftValues(item: Item, draft: ItemDraft, baseline: ItemDraft, onlyTyped = false): ItemProperties {
  const typed = (name: string) => !onlyTyped || draft[name]?.trim() !== baseline[name]?.trim();
  const read = (name: string, label: string, min: number, max: number, own: number) => typed(name) ? readDraft(draft, name, label, min, max) : own;
  const size = { w: read('width', t('Width'), .001, 100, item.size.w), d: read('depth', t('Depth'), .001, 100, item.size.d), h: read('height', t('Height'), .001, 100, item.size.h) };
  let elevation = read('elevation', t('#PropsDistanceFromFloor'), 0, 100, item.elevation);
  // What hangs from the ceiling — a lamp, a beam, a mansard — keeps its top there when only its height changes.
  if (item.placement === 'ceiling' && draft.elevation === baseline.elevation && size.h !== item.size.h) elevation = Math.max(0, item.elevation + item.size.h - size.h);
  return { size, position: { x: read('x', t('#PropsCoordinateX'), -10000, 10000, item.position.x), z: read('z', t('#PropsCoordinateY'), -10000, 10000, item.position.z) },
    elevation, rotation: typed('rotation') ? -readDraft(draft, 'rotation', t('#PropsRotation'), -36000, 36000) * Math.PI / 180 : item.rotation };
}

export function applyItemProperties(target: Item, values: ItemProperties, replacement?: CatalogItem | null): void {
  const desired = [values.size.w, values.size.h, values.size.d];
  const previous = replacement?.size ?? [target.size.w, target.size.h, target.size.d];
  if (![...desired, ...previous].every((value) => Number.isFinite(value) && value > 0)) throw new Error(t('#ItemPropsSizeMustBePositive'));
  if (![values.position.x, values.position.z, values.elevation, values.rotation].every(Number.isFinite)) throw new Error(t('#ItemPropsCheckPosition'));
  const baseScale = replacement ? [1, 1, 1] : target.scale;
  if (baseScale.length !== 3 || !baseScale.every((value) => Number.isFinite(value) && value !== 0)) throw new Error(t('#ItemPropsBadBaseScale'));
  // Assemblies use authored scale; keep their protruding doors and signed mirroring
  // while changing the same dimension that the 2D footprint displays.
  const scale = baseScale.map((value, axis) => value * desired[axis] / previous[axis]) as Item['scale'];
  if (!scale.every(Number.isFinite)) throw new Error(t('#ItemPropsCantApplySize'));
  if (replacement) {
    Object.assign(target, {
      modelRef: { ...replacement.modelRef },
      caption: replacement.title, tags: [...replacement.tags],
      mirrored: false, pivot: 'bottom', materials: undefined, modules: undefined,
      light: undefined, usesExplicitTiling: undefined,
      variationId: undefined, price: undefined, url: undefined,
    });
  }
  Object.assign(target, values, { scale });
}
